/**
 * A moldura: a navegação de sempre e o lugar onde a tela entra.
 *
 * Uma lista só de destinos, usada de dois jeitos pelo CSS: barra embaixo no
 * celular, coluna à esquerda no computador. Duas navegações separadas
 * desencontrariam na primeira tela nova.
 */

import { useEffect } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Icone, type NomeDoIcone } from './Icone';
import { traduzir, usarEstadoDoArmazem } from '@nosso-sempre/shared';
import { usarTocador } from '../nucleo/tocador';
import './moldura.css';

interface Destino {
  para: string;
  nome: string;
  icone: NomeDoIcone;
  /**
   * A cor da seção, a mesma do `hue` do atalho correspondente no painel do
   * Flutter. Onde lá não existe atalho — cinema e ajustes —, vale o destaque
   * que a própria tela usa: `NetflixColors.accent` é o deepRose, e os ajustes
   * ficam com o destaque secundário do tema.
   */
  cor: string;
}

/**
 * O `nome` fica em português aqui e passa pelo `traduzir()` só na hora de
 * desenhar: esta lista é lida uma vez quando o arquivo é importado, e quem
 * trocar de idioma depois já perdeu a chance de ela ser lida de novo.
 */
export const DESTINOS: Destino[] = [
  { para: '/', nome: 'Casa', icone: 'casa', cor: 'var(--rosa-quartzo)' },
  { para: '/cinema', nome: 'Cinema', icone: 'cinema', cor: 'var(--rosa)' },
  { para: '/kovu', nome: 'Kovu', icone: 'kovu', cor: 'var(--aurora-ouro)' },
  { para: '/lembrancas', nome: 'Lembranças', icone: 'estrela', cor: 'var(--lavanda-viva)' },
  { para: '/desejos', nome: 'Desejos', icone: 'desejo', cor: 'var(--aurora-agua)' },
  { para: '/ajustes', nome: 'Ajustes', icone: 'ajustes', cor: 'var(--lavanda)' },
];

/**
 * O fundo de cada tela. No Flutter é um `Positioned.fill` dentro do `Scaffold`,
 * e três telas trocam o dele: o painel, o Kovu e o cinema. As outras herdam o
 * padrão, que mora no `body`.
 */
const FUNDOS: Record<string, string> = {
  '/': 'fundo-painel',
  '/kovu': 'fundo-kovu',
  '/kovu-today': 'fundo-kovu-hoje',
  '/kovu-trivia': 'fundo-kovu-quiz',
  '/kovu-memory': 'fundo-kovu-caderno',
  '/cinema': 'fundo-cinema',
};

export function Moldura() {
  const { erro } = usarEstadoDoArmazem();
  const { pathname } = useLocation();
  const fundo = FUNDOS[pathname];
  /* A barra do tocador é fixa, então não empurra nada sozinha: sem esta marca
     ela cobriria o fim de cada tela.

     E ela tem duas alturas: com mais de uma faixa a fila de botões não cabe ao
     lado do nome e desce para uma terceira linha. Quem decide isso é o
     `disco` de `BarraDoTocador.tsx`, e a conta é refeita aqui porque a barra
     nasce fora desta moldura -- a marca dela não desce até o palco por cascata
     nenhuma. Os dois lados leem a mesma coisa (`faixas.length > 1`), então não
     há como desencontrarem. */
  const atual = usarTocador().atual;
  const disco = atual !== null && atual.gravacao.faixas.length > 1;

  /* A barra mora na raiz, acima das rotas, e precisa saber se há uma barra de
     navegação embaixo dela para não cobri-la. Quem sabe isso é a moldura, que
     ou está em cena ou não está -- e uma marca no `body` é o único caminho que
     uma folha de estilo tem para subir da moldura até a raiz. */
  useEffect(() => {
    document.body.classList.add('com-moldura');
    return () => document.body.classList.remove('com-moldura');
  }, []);

  return (
    <div
      className={
        atual === null ? 'moldura' : disco ? 'moldura com-tocador com-faixas' : 'moldura com-tocador'
      }
    >
      {fundo ? <div className={`fundo ${fundo}`} aria-hidden="true" /> : null}

      <nav className="navegacao" aria-label={traduzir('Seções')}>
        <span className="moldura-marca">{traduzir('Nosso Sempre')}</span>
        {DESTINOS.map((d) => (
          <NavLink
            key={d.para}
            to={d.para}
            end={d.para === '/'}
            className={({ isActive }) => `destino${isActive ? ' aqui' : ''}`}
            style={{ ['--cor-secao' as string]: d.cor }}
          >
            <Icone nome={d.icone} />
            <span>{traduzir(d.nome)}</span>
          </NavLink>
        ))}
      </nav>

      <main className="palco">
        {erro ? (
          <p className="fora-do-ar" role="status">
            <Icone nome="aviso" tamanho={16} />
            {traduzir('Sem conexão ao vivo. O que aparece aqui pode estar atrasado.')}
          </p>
        ) : null}
        <Outlet />
      </main>
    </div>
  );
}
